import { Component, OnInit, OnDestroy, ViewChild, Input } from '@angular/core';
import { Router } from '@angular/router';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { StandortService, Standort } from '../services/standort.service';
import { KundeService, Kunde } from '../services/kunde.service';
import { MitarbeiterService, TechnicianOption } from '../services/mitarbeiter.service';
import { TableExportService, ExportColumn } from '../services/table-export.service';
import { PaginationService } from '../core/services/pagination.service';
import { LocalStorageFilterService } from '../core/services/local-storage-filter.service';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-standorte',
  templateUrl: './standorte.component.html',
  styleUrls: ['./standorte.component.scss']
})
export class StandorteComponent implements OnInit, OnDestroy {
  @Input() kundeId: string;
  @Input() isEmbedded: boolean = false;
  @Input() parentContext?: string;
  
  displayedColumns: string[] = ['name', 'techniker', 'adresse', 'intervall', 'status', 'action'];
  dataSource: MatTableDataSource<Standort>;
  
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  loading = false;
  totalItems = 0;
  currentPage = 1;
  pageIndex = 0; // 0-based page index for mat-paginator
  pageSize: number;
  pageSizeOptions: number[];
  searchTerm = '';

  // Filter properties
  selectedStatus: number | null = 1; // Default to active (1), null = all, 1 = active, 2 = inactive
  selectedKunde: Kunde | null = null;
  selectedTechniker: TechnicianOption | null = null;
  selectedAlphabetFilter: string | null = null;
  statusDropdownOpen = false;
  // Worklist filter: '' = all, 'ohne_vertrag', 'ohne_service'
  selectedVertrag: '' | 'ohne_vertrag' | 'ohne_service' = '';
  vertragDropdownOpen = false;
  
  // Autocomplete properties
  kundeSearchValue = '';
  technikerSearchValue = '';
  showKundeAutocomplete = false;
  showTechnikerAutocomplete = false;
  filteredKunden: Kunde[] = [];
  filteredTechniker: TechnicianOption[] = [];
  private searchTimeout: any;
  
  // Dropdown data
  kunden: Kunde[] = [];
  techniker: TechnicianOption[] = [];

  // LocalStorage keys
  private readonly STORAGE_KEY_SEARCH = 'standorte_search_history';
  private readonly STORAGE_KEY_FILTERS = 'standorte_filters';
  private readonly STORAGE_KEY_PAGINATION = 'standorte_pagination';
  searchHistory: string[] = [];
  showSearchHistory = false;

  constructor(
    private standortService: StandortService,
    private kundeService: KundeService,
    private mitarbeiterService: MitarbeiterService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private router: Router,
    private tableExportService: TableExportService,
    private paginationService: PaginationService,
    private localStorageService: LocalStorageFilterService
  ) {
    this.dataSource = new MatTableDataSource<Standort>([]);
    // Initialize pagination from service
    this.pageSize = this.paginationService.getPageSize();
    this.pageSizeOptions = this.paginationService.getPageSizeOptions();
  }

  ngOnInit(): void {
    console.log('✅ StandorteComponent ngOnInit - Component loaded successfully');
    console.log('📍 Current URL:', window.location.href);
    console.log('🎯 Component should be loaded within shell');
    console.log('🔍 Component Context:', {
      isEmbedded: this.isEmbedded,
      parentContext: this.parentContext
    });
    
    if (this.isEmbedded) {
      this.showEmbeddedUI();
    } else {
      this.showStandaloneUI();
    }
    
    // Load from localStorage first
    this.loadFromLocalStorage();
    
    this.loadDropdowns();
    this.loadStandorte();
    
    // Add click outside listener to close dropdown
    document.addEventListener('click', this.onDocumentClick.bind(this));
  }

  ngOnDestroy(): void {
    // Remove click outside listener
    document.removeEventListener('click', this.onDocumentClick.bind(this));
    // Clear search timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  }

  onDocumentClick(event: Event): void {
    // Close status dropdown when clicking outside
    this.statusDropdownOpen = false;
    this.vertragDropdownOpen = false;
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  /**
   * Load search history and filters from localStorage
   */
  loadFromLocalStorage(): void {
    // Load search history
    this.searchHistory = this.localStorageService.getSearchHistory(this.STORAGE_KEY_SEARCH);

    // Load filters (status only - kunde and techniker resolved in loadDropdowns)
    const savedFilters = this.localStorageService.loadFilters(this.STORAGE_KEY_FILTERS);
    
    // When embedded in client-edit, show all standorte (active + inactive) by default
    // Only apply saved status filter if NOT embedded in client-edit
    if (this.isEmbedded && this.parentContext === 'client-edit') {
      // In client-edit context, default to null (all statuses)
      this.selectedStatus = null;
    } else if (savedFilters) {
      // In standalone mode, use saved filter or default to active
      this.selectedStatus = savedFilters.selectedStatus ?? 1;
      if (savedFilters.selectedAlphabetFilter) {
        this.selectedAlphabetFilter = savedFilters.selectedAlphabetFilter;
      }
      this.selectedVertrag = this.isEmbedded ? '' : (savedFilters.selectedVertrag ?? '');
    } else {
      // No saved filters and not in client-edit context, default to active
      this.selectedStatus = 1;
    }

    // Load pagination
    const savedPagination = this.localStorageService.loadPagination(this.STORAGE_KEY_PAGINATION);
    if (savedPagination) {
      this.currentPage = savedPagination.currentPage || 1;
      this.pageSize = savedPagination.pageSize || this.pageSize;
      this.pageIndex = this.currentPage - 1;
    }

    // Load search term - but NOT when embedded in client-edit
    // In client-edit context, we want to show ALL standorte for the client without any search filter
    if (this.isEmbedded && this.parentContext === 'client-edit') {
      // In client-edit context, don't use search term (show all standorte)
      this.searchTerm = '';
    } else if (savedFilters?.searchTerm) {
      // In standalone mode, use saved search term
      this.searchTerm = savedFilters.searchTerm;
    }
  }

  /**
   * Save current filters to localStorage
   */
  saveFiltersToLocalStorage(): void {
    const filters = {
      selectedStatus: this.selectedStatus,
      kundeId: this.selectedKunde?.id || this.selectedKunde?.uuid || null,
      technikerId: this.selectedTechniker?.id || null,
      searchTerm: this.searchTerm || '',
      selectedAlphabetFilter: this.selectedAlphabetFilter,
      selectedVertrag: this.selectedVertrag
    };
    this.localStorageService.saveFilters(this.STORAGE_KEY_FILTERS, filters);
  }

  /**
   * Save pagination state to localStorage
   */
  savePaginationToLocalStorage(): void {
    this.localStorageService.savePagination(this.STORAGE_KEY_PAGINATION, this.currentPage, this.pageSize);
  }

  loadStandorte(page: number = 1): void {
    this.loading = true;
    this.currentPage = page;
    this.pageIndex = page - 1; // Convert 1-based to 0-based for mat-paginator

    // When embedded in client-edit, always clear search term (show all standorte for the client)
    // Clear it in the component state as well so the UI reflects this
    if (this.isEmbedded && this.parentContext === 'client-edit') {
      this.searchTerm = '';
    }
    const searchTermToUse = this.searchTerm;

    this.standortService.getStandorte(
      page, 
      this.pageSize, 
      searchTermToUse, 
      this.sort?.active || 'name', 
      this.sort?.direction || 'asc',
      this.selectedKunde?.uuid || this.kundeId,
      this.selectedStatus !== null ? this.selectedStatus : undefined,
      this.selectedTechniker?.id ? Number(this.selectedTechniker.id) : undefined,
      this.selectedAlphabetFilter || undefined,
      this.selectedVertrag || undefined
    ).subscribe({
      next: (response) => {
        this.dataSource.data = response.data;
        this.totalItems = response.total || 0;
        this.loading = false;
        
        // Update paginator page index if it exists
        if (this.paginator) {
          this.paginator.pageIndex = this.pageIndex;
        }
      },
      error: (error) => {
        console.error('Error loading standorte:', error);
        this.snackBar.open('Fehler beim Laden der Standorte', 'Schließen', { duration: 3000 });
        this.loading = false;
      }
    });
  }

  /**
   * Load dropdown data for filters
   */
  loadDropdowns(): void {
    // Load clients
    this.kundeService.getActiveKundenForDropdown().subscribe({
      next: (response) => {
        this.kunden = response.data || [];
        this.filteredKunden = this.kunden;
      },
      error: (error) => {
        console.error('Error loading kunden:', error);
      }
    });

    // Load technicians (all active, already sorted by nachname/vorname)
    this.mitarbeiterService.getTechnicians().subscribe({
      next: (response) => {
        this.techniker = response.data || [];
        this.filteredTechniker = this.techniker;
      },
      error: (error) => {
        console.error('Error loading techniker:', error);
      }
    });
  }

  onSearch(): void {
    this.loadStandorte(1);
  }

  /**
   * Handle search input with debounce
   */
  onSearchInput(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.searchTerm = filterValue;
    
    // Hide search history dropdown when typing
    this.showSearchHistory = false;
    
    // Save filters (including searchTerm)
    this.saveFiltersToLocalStorage();
    
    // Clear existing timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    
    // Set new timeout for debounced search (300ms delay)
    this.searchTimeout = setTimeout(() => {
      // Save to search history only when search is executed (after debounce)
      if (this.searchTerm && this.searchTerm.trim()) {
        this.localStorageService.saveSearchToHistory(this.STORAGE_KEY_SEARCH, this.searchTerm);
        this.searchHistory = this.localStorageService.getSearchHistory(this.STORAGE_KEY_SEARCH);
      }
      this.pageIndex = 0;
      this.currentPage = 1;
      this.savePaginationToLocalStorage();
      if (this.paginator) {
        this.paginator.firstPage();
      }
      this.loadStandorte(1);
    }, 300);
  }

  /**
   * Show search history dropdown on focus
   */
  onSearchFocus(): void {
    // Reload search history from localStorage
    this.searchHistory = this.localStorageService.getSearchHistory(this.STORAGE_KEY_SEARCH);
    if (this.searchHistory.length > 0) {
      this.showSearchHistory = true;
    }
  }

  /**
   * Hide search history dropdown on blur (with delay to allow click)
   */
  onSearchBlur(): void {
    setTimeout(() => {
      this.showSearchHistory = false;
    }, 200);
  }

  /**
   * Select a search term from history
   */
  selectSearchHistory(term: string): void {
    this.searchTerm = term;
    this.showSearchHistory = false;
    
    // Save to search history (moves to top)
    this.localStorageService.saveSearchToHistory(this.STORAGE_KEY_SEARCH, term);
    this.searchHistory = this.localStorageService.getSearchHistory(this.STORAGE_KEY_SEARCH);
    
    // Save to filters localStorage to persist the selected search value
    this.saveFiltersToLocalStorage();
    
    // Trigger search
    this.pageIndex = 0;
    this.currentPage = 1;
    this.savePaginationToLocalStorage();
    if (this.paginator) {
      this.paginator.firstPage();
    }
    this.loadStandorte(1);
  }

  clearSearchHistory(): void {
    this.localStorageService.clearSearchHistory(this.STORAGE_KEY_SEARCH);
    this.searchHistory = [];
    this.showSearchHistory = false;
    
    // Also clear the searchTerm from filters localStorage
    // This prevents the filter from being preselected on next navigation
    const savedFilters = this.localStorageService.loadFilters(this.STORAGE_KEY_FILTERS);
    if (savedFilters) {
      savedFilters.searchTerm = '';
      this.localStorageService.saveFilters(this.STORAGE_KEY_FILTERS, savedFilters);
    }
  }

  /**
   * Clear search and reload
   */
  clearSearch(): void {
    this.searchTerm = '';
    
    // Save to filters localStorage to persist the cleared state
    this.saveFiltersToLocalStorage();
    
    this.pageIndex = 0;
    if (this.paginator) {
      this.paginator.firstPage();
    }
    this.loadStandorte(1);
  }

  /**
   * Handle alphabet filter click
   */
  onAlphabetFilterClick(letter: string | null): void {
    // If null is passed (Alle button), clear filter
    // Otherwise, toggle: if same letter clicked, clear filter; otherwise set new filter
    if (letter === null) {
      this.selectedAlphabetFilter = null;
    } else if (this.selectedAlphabetFilter === letter) {
      this.selectedAlphabetFilter = null;
    } else {
      this.selectedAlphabetFilter = letter;
    }
    
    // Reset to first page
    this.pageIndex = 0;
    this.currentPage = 1;
    if (this.paginator) {
      this.paginator.firstPage();
    }
    
    // Save to localStorage
    this.saveFiltersToLocalStorage();
    this.savePaginationToLocalStorage();
    
    // Reload data
    this.loadStandorte(1);
  }

  /**
   * Get status label for display
   */
  getStatusLabel(): string {
    if (this.selectedStatus === null) {
      return 'Alle Status';
    } else if (this.selectedStatus === 1) {
      return 'Aktiv';
    } else if (this.selectedStatus === 2) {
      return 'Inaktiv';
    }
    return 'Alle Status';
  }

  getVertragLabel(): string {
    if (this.selectedVertrag === 'ohne_vertrag') {
      return 'Ohne Vertrag';
    } else if (this.selectedVertrag === 'ohne_service') {
      return 'Ohne Service';
    }
    return 'Alle Verträge';
  }

  toggleVertragDropdown(event: Event): void {
    event.stopPropagation();
    this.statusDropdownOpen = false;
    this.vertragDropdownOpen = !this.vertragDropdownOpen;
  }

  onVertragChange(vertrag: '' | 'ohne_vertrag' | 'ohne_service'): void {
    this.selectedVertrag = vertrag;
    this.vertragDropdownOpen = false;
    this.pageIndex = 0;
    this.currentPage = 1;
    this.saveFiltersToLocalStorage();
    this.savePaginationToLocalStorage();
    if (this.paginator) {
      this.paginator.firstPage();
    }
    this.loadStandorte(1);
  }

  /**
   * Toggle status dropdown
   */
  toggleStatusDropdown(event: Event): void {
    event.stopPropagation();
    this.vertragDropdownOpen = false;
    this.statusDropdownOpen = !this.statusDropdownOpen;
  }

  /**
   * Status filter change
   */
  onStatusChange(statusId: number | null): void {
    this.selectedStatus = statusId;
    this.statusDropdownOpen = false;
    this.pageIndex = 0;
    this.currentPage = 1;
    this.saveFiltersToLocalStorage();
    this.savePaginationToLocalStorage();
    if (this.paginator) {
      this.paginator.firstPage();
    }
    this.loadStandorte(1);
  }

  /**
   * Kunde autocomplete search
   */
  onKundeSearch(event: any): void {
    const value = event.target.value;
    this.kundeSearchValue = value;
    
    if (value.length > 0) {
      this.filteredKunden = this.kunden.filter(kunde => 
        kunde.suchbegriff?.toLowerCase().includes(value.toLowerCase())
      );
    } else {
      this.filteredKunden = this.kunden;
    }
  }

  onKundeBlur(): void {
    setTimeout(() => {
      this.showKundeAutocomplete = false;
    }, 200);
  }

  selectKunde(kunde: Kunde): void {
    this.selectedKunde = kunde;
    this.kundeSearchValue = kunde.suchbegriff || '';
    this.showKundeAutocomplete = false;
    this.currentPage = 1;
    this.saveFiltersToLocalStorage();
    this.savePaginationToLocalStorage();
    this.loadStandorte(1);
  }

  clearKundeFilter(): void {
    this.selectedKunde = null;
    this.kundeSearchValue = '';
    this.currentPage = 1;
    this.saveFiltersToLocalStorage();
    this.savePaginationToLocalStorage();
    this.loadStandorte(1);
  }

  getKundeDisplayValue(): string {
    if (this.selectedKunde) {
      return this.selectedKunde.suchbegriff || '';
    }
    return this.kundeSearchValue;
  }

  /**
   * Techniker autocomplete search
   */
  onTechnikerSearch(event: any): void {
    const value = event.target.value;
    this.technikerSearchValue = value;
    
    if (value.length > 0) {
      this.filteredTechniker = this.techniker.filter(tech => 
        tech.display_name?.toLowerCase().includes(value.toLowerCase())
      );
    } else {
      this.filteredTechniker = this.techniker;
    }
  }

  onTechnikerBlur(): void {
    setTimeout(() => {
      this.showTechnikerAutocomplete = false;
    }, 200);
  }

  selectTechniker(techniker: TechnicianOption): void {
    this.selectedTechniker = techniker;
    this.technikerSearchValue = techniker.display_name;
    this.showTechnikerAutocomplete = false;
    this.currentPage = 1;
    this.saveFiltersToLocalStorage();
    this.savePaginationToLocalStorage();
    this.loadStandorte(1);
  }

  clearTechnikerFilter(): void {
    this.selectedTechniker = null;
    this.technikerSearchValue = '';
    this.currentPage = 1;
    this.saveFiltersToLocalStorage();
    this.savePaginationToLocalStorage();
    this.loadStandorte(1);
  }

  getTechnikerDisplayValue(): string {
    if (this.selectedTechniker) {
      return this.selectedTechniker.display_name;
    }
    return this.technikerSearchValue;
  }

  onPageChange(event: any): void {
    this.pageSize = event.pageSize;
    this.paginationService.setPageSize(event.pageSize);
    this.pageIndex = event.pageIndex;
    this.currentPage = event.pageIndex + 1;
    this.savePaginationToLocalStorage();
    this.loadStandorte(event.pageIndex + 1);
  }

  onSortChange(event: any): void {
    this.loadStandorte(1);
  }

  addStandort(): void {
    if (this.isEmbedded && this.parentContext === 'client-edit') {
      // In embedded mode within client-edit, navigate to standort-edit with return context
      this.router.navigate(['/standort-edit/new'], { 
        queryParams: { 
          returnTo: 'client-edit',
          kundeId: this.kundeId 
        } 
      });
    } else {
      // Standalone mode - normal navigation
      this.router.navigate(['/standort-edit/new']);
    }
  }



  editStandort(standort: Standort): void {
    if (this.isEmbedded && this.parentContext === 'client-edit') {
      // In embedded mode within client-edit, navigate to standort-edit with return context
      this.router.navigate(['/standort-edit', standort.id.toString()], { 
        queryParams: { 
          returnTo: 'client-edit',
          kundeId: this.kundeId 
        } 
      });
    } else {
      // Standalone mode - normal navigation
      this.showPageDetail(standort.id.toString());
    }
  }

  showEmbeddedUI(): void {
    console.log('🎯 Showing embedded UI for standorte component');
    // Hide header elements, adjust styling for embedded view
    // This will be handled in the template with conditional rendering
  }

  showStandaloneUI(): void {
    console.log('🎯 Showing standalone UI for standorte component');
    // Show full header, navigation, etc. for standalone view
    // This will be handled in the template with conditional rendering
  }

  showPageDetail(standortId: string): void {
    console.log('showPageDetail called with standortId:', standortId, 'type:', typeof standortId);

    // Validate standortId
    if (!standortId || standortId === '0' || standortId === 'new') {
      console.log('Invalid standortId, navigating to new location form');
      if (this.isEmbedded && this.parentContext === 'client-edit') {
        this.router.navigate(['/standort-edit/new'], { 
          queryParams: { 
            returnTo: 'client-edit',
            kundeId: this.kundeId 
          } 
        });
      } else {
        this.router.navigate(['/standort-edit/new']);
      }
    } else {
      console.log('Navigating to edit location with ID:', standortId);
      if (this.isEmbedded && this.parentContext === 'client-edit') {
        this.router.navigate(['/standort-edit', standortId], { 
          queryParams: { 
            returnTo: 'client-edit',
            kundeId: this.kundeId 
          } 
        });
      } else if (this.selectedVertrag) {
        this.router.navigate(['/standort-edit', standortId], { queryParams: { tab: 'abrechnung' } });
      } else {
        this.router.navigate(['/standort-edit', standortId]);
      }
    }
  }

  getStatusText(status: any): string {
    if (!status) {
      return 'Kein Status';
    }
    
    // Handle case where status is an object (e.g., {id: 1, beschreibung: 'Aktiv'})
    if (typeof status === 'object' && status.id) {
      const statusFromEnv = (environment as any).standorte_status?.find((s: any) => s.id === status.id);
      return statusFromEnv ? statusFromEnv.label : (status.beschreibung || `Status ${status.id}`);
    }
    
    // Handle case where status is a number
    if (typeof status === 'number') {
      const statusFromEnv = (environment as any).standorte_status?.find((s: any) => s.id === status);
      return statusFromEnv ? statusFromEnv.label : `Status ${status}`;
    }
    
    return 'Unbekannt';
  }

  /**
   * Get CSS classes for status badge based on status object
   */
  getStatusClasses(status: any): string {
    if (status && status.bg_color && status.text_color && status.ring_color && 
        status.dark_bg_color && status.dark_text_color && status.dark_ring_color) {
      return `${status.bg_color} ${status.text_color} ${status.ring_color} ${status.dark_bg_color} ${status.dark_text_color} ${status.dark_ring_color}`;
    }
    
    // Fallback for old system or missing status data
    let statusId: number;
    
    // Handle case where status is an object
    if (typeof status === 'object' && status.id) {
      statusId = status.id;
    } else if (typeof status === 'number') {
      statusId = status;
    } else {
      return 'bg-slate-50 text-slate-700 ring-slate-600/20 dark:bg-slate-900/30 dark:text-slate-400 dark:ring-slate-500/50';
    }
    
    // Return appropriate classes based on status ID
    switch (statusId) {
      case 1:
        return 'bg-green-50 text-green-700 ring-green-600/20 dark:bg-green-900/30 dark:text-green-400 dark:ring-green-500/50';
      case 2:
        return 'bg-red-50 text-red-700 ring-red-600/20 dark:bg-red-900/30 dark:text-red-400 dark:ring-red-500/50';
      case 3:
        return 'bg-yellow-50 text-yellow-700 ring-yellow-600/20 dark:bg-yellow-900/30 dark:text-yellow-400 dark:ring-yellow-500/50';
      case 4:
        return 'bg-gray-50 text-gray-700 ring-gray-600/20 dark:bg-gray-900/30 dark:text-gray-400 dark:ring-gray-500/50';
      default:
        return 'bg-slate-50 text-slate-700 ring-slate-600/20 dark:bg-slate-900/30 dark:text-slate-400 dark:ring-slate-500/50';
    }
  }

  /**
   * Get client name from standort
   */
  getClientName(standort: Standort): string {
    if (standort.kunde?.suchbegriff) {
      return standort.kunde.suchbegriff;
    }
    return '-';
  }

  openClientEdit(standort: Standort): void {
    const uuid = standort.kunde?.uuid || standort.kunde_id;
    if (uuid) {
      this.router.navigate(['/client-edit', uuid]);
    }
  }

  /**
   * Get technician name from standort
   */
  getTechnicianName(standort: Standort): string {
    if (standort.techniker) {
      const parts: string[] = [];
      if (standort.techniker.vorname) parts.push(standort.techniker.vorname);
      if (standort.techniker.nachname) parts.push(standort.techniker.nachname);
      if (parts.length > 0) {
        return parts.join(' ');
      }
    }
    return '-';
  }

  /**
   * Get formatted address from standort
   */
  getFormattedAddress(standort: Standort): string {
    const parts: string[] = [];
    
    if (standort.strasse) {
      parts.push(standort.strasse);
    }
    
    if (standort.plz && standort.ort) {
      parts.push(`${standort.plz} ${standort.ort}`);
    } else if (standort.plz) {
      parts.push(standort.plz);
    } else if (standort.ort) {
      parts.push(standort.ort);
    }
    
    return parts.length > 0 ? parts.join(', ') : '-';
  }

  /**
   * Format bericht_email JSON array to show "Name (email); Name (email)"
   */
  formatBerichtEmail(berichtEmail: any): string {
    if (!berichtEmail) return '-';
    
    try {
      // If it's already a string, try to parse it
      let parsed: any;
      if (typeof berichtEmail === 'string') {
        parsed = JSON.parse(berichtEmail);
      } else {
        parsed = berichtEmail;
      }
      
      // Handle array of objects
      if (Array.isArray(parsed)) {
        if (parsed.length === 0) return '-';
        return parsed
          .map((item: any) => {
            // Handle different possible structures
            const name = item.name || item.Name || item.email || '';
            const email = item.email || item.Email || '';
            if (name && email) {
              return `${name} (${email})`;
            } else if (email) {
              return email;
            } else if (name) {
              return name;
            }
            return '';
          })
          .filter((item: string) => item !== '')
          .join('; ');
      }
      
      // Handle single object
      if (typeof parsed === 'object') {
        const name = parsed.name || parsed.Name || '';
        const email = parsed.email || parsed.Email || '';
        if (name && email) {
          return `${name} (${email})`;
        } else if (email) {
          return email;
        } else if (name) {
          return name;
        }
      }
      
      // If it's a simple string, return as-is
      return String(parsed);
    } catch (error) {
      // If parsing fails, return as string
      return String(berichtEmail);
    }
  }

  /**
   * Get count of active indikatoren for a standort
   * Note: This calculates from objektstruktur data if available, otherwise returns '-'
   */
  getAnzahlAktiveIndikatoren(standort: Standort): string {
    // Check if objektstruktur data is available in standort
    if ((standort as any).objektstruktur_indikatoren) {
      try {
        const objektstruktur = typeof (standort as any).objektstruktur_indikatoren === 'string' 
          ? JSON.parse((standort as any).objektstruktur_indikatoren)
          : (standort as any).objektstruktur_indikatoren;
        
        if (Array.isArray(objektstruktur)) {
          // Count active indikatoren (status = 1 or effectively active)
          let count = 0;
          objektstruktur.forEach((gebaeude: any) => {
            if (gebaeude.sektionen) {
              gebaeude.sektionen.forEach((sektion: any) => {
                if (sektion.raeume) {
                  sektion.raeume.forEach((raum: any) => {
                    if (raum.indikatoren) {
                      raum.indikatoren.forEach((indikator: any) => {
                        // Count indikatoren that are active (status = 1 or true)
                        if (indikator.status === 1 || indikator.status === true || indikator.status === '1') {
                          count++;
                        }
                      });
                    }
                  });
                }
              });
            }
          });
          return count.toString();
        }
      } catch (error) {
        console.error('Error parsing objektstruktur_indikatoren:', error);
      }
    }
    
    // If data not available, return placeholder
    return '-';
  }

  /**
   * Get periodizitaet label from active projektaufgabe
   */
  getPeriodizitaetLabel(standort: Standort): string {
    if (!standort.active_projektaufgabe || !standort.active_projektaufgabe.periodizitaet) {
      return '-';
    }
    
    const periodizitaetId = standort.active_projektaufgabe.periodizitaet;
    
    // Try to find in environment.projektaufgabe_periodizitaet first (deprecated)
    const periodizitaetArray = (environment as any).projektaufgabe_periodizitaet || [];
    let periodizitaet = periodizitaetArray.find((p: any) => p.id === periodizitaetId);
    
    // If not found, try periode_aufgaben (new)
    if (!periodizitaet) {
      const periodeAufgabenArray = (environment as any).periode_aufgaben || [];
      periodizitaet = periodeAufgabenArray.find((p: any) => p.id === periodizitaetId);
    }
    
    return periodizitaet ? periodizitaet.label : `ID: ${periodizitaetId}`;
  }

  /**
   * Get periodizitat_serie label from active projektaufgabe
   * Returns formatted text for gerade/ungerade or erstes/zweites/drittes
   */
  getPeriodizitatSerieLabel(standort: Standort): string {
    if (!standort.active_projektaufgabe || !standort.active_projektaufgabe.periodizitat_serie) {
      return '';
    }
    
    const periodizitaetId = standort.active_projektaufgabe.periodizitaet;
    const periodizitatSerie = standort.active_projektaufgabe.periodizitat_serie;
    
    // Only show for alle_2_monate (3) or quartalsweise (4)
    if (periodizitaetId !== 3 && periodizitaetId !== 4) {
      return '';
    }
    
    if (periodizitatSerie === 'gerade') {
      return 'Gerade Monate';
    } else if (periodizitatSerie === 'ungerade') {
      return 'Ungerade Monate';
    } else if (periodizitatSerie === 'erstes') {
      return 'Erstes Monat';
    } else if (periodizitatSerie === 'zweites') {
      return 'Zweites Monat';
    } else if (periodizitatSerie === 'drittes') {
      return 'Drittes Monat';
    }
    
    return '';
  }

  /**
   * Format date for export
   */
  formatDate(dateString: string | undefined): string {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}.${month}.${year}`;
    } catch (error) {
      return '-';
    }
  }

  /**
   * Export table data to CSV
   */
  exportToCSV(): void {
    try {
      const exportColumns: ExportColumn<Standort>[] = [
        { key: 'name', label: 'Name' },
        { key: 'kunde.suchbegriff', label: 'Kunde', transform: (val, row) => this.getClientName(row) },
        { key: 'techniker.vorname', label: 'Techniker', transform: (val, row) => this.getTechnicianName(row) },
        { key: 'strasse', label: 'Straße' },
        { key: 'plz', label: 'PLZ' },
        { key: 'ort', label: 'Ort' },
        { key: 'land', label: 'Land' },
        { key: 'adresse', label: 'Adresse', transform: (val, row) => this.getFormattedAddress(row) },
        { key: 'active_projektaufgabe.periodizitaet', label: 'Intervall', transform: (val, row) => this.getPeriodizitaetLabel(row) },
        { key: 'datum_ab_wann', label: 'Datum ab wann', transform: (val) => this.formatDate(val) },
        { key: 'datum_bis', label: 'Datum bis', transform: (val) => this.formatDate(val) },
        { key: 'bericht_email', label: 'Bericht E-Mail', transform: (val) => this.formatBerichtEmail(val) },
        { key: 'anmeldung_besuche', label: 'Anmeldung Besuche' },
        { key: 'status', label: 'Status', transform: (val) => this.getStatusText(val) },
        { key: 'bemerkung_struktur_anmerkung', label: 'Bemerkung' },
        { key: 'anzahl_aktive_indikatoren', label: 'Anzahl aktive Indikatoren', transform: (val, row) => this.getAnzahlAktiveIndikatoren(row) }
      ];

      this.tableExportService.exportTable({
        data: this.dataSource.data,
        columns: exportColumns,
        filename: 'standorte',
        format: 'csv',
        includeHeaders: true
      });

      this.snackBar.open('Standorte erfolgreich als CSV exportiert', 'Schließen', { duration: 3000 });
    } catch (error) {
      console.error('Error exporting standorte:', error);
      this.snackBar.open('Fehler beim Exportieren der Standorte', 'Schließen', { duration: 3000 });
    }
  }

  /**
   * Export table data to Excel
   */
  async exportToExcel(): Promise<void> {
    try {
      const exportColumns: ExportColumn<Standort>[] = [
        { key: 'name', label: 'Name' },
        { key: 'kunde.suchbegriff', label: 'Kunde', transform: (val, row) => this.getClientName(row) },
        { key: 'techniker.vorname', label: 'Techniker', transform: (val, row) => this.getTechnicianName(row) },
        { key: 'strasse', label: 'Straße' },
        { key: 'plz', label: 'PLZ' },
        { key: 'ort', label: 'Ort' },
        { key: 'land', label: 'Land' },
        { key: 'adresse', label: 'Adresse', transform: (val, row) => this.getFormattedAddress(row) },
        { key: 'active_projektaufgabe.periodizitaet', label: 'Intervall', transform: (val, row) => this.getPeriodizitaetLabel(row) },
        { key: 'datum_ab_wann', label: 'Datum ab wann', transform: (val) => this.formatDate(val) },
        { key: 'datum_bis', label: 'Datum bis', transform: (val) => this.formatDate(val) },
        { key: 'bericht_email', label: 'Bericht E-Mail', transform: (val) => this.formatBerichtEmail(val) },
        { key: 'anmeldung_besuche', label: 'Anmeldung Besuche' },
        { key: 'status', label: 'Status', transform: (val) => this.getStatusText(val) },
        { key: 'bemerkung_struktur_anmerkung', label: 'Bemerkung' },
        { key: 'anzahl_aktive_indikatoren', label: 'Anzahl aktive Indikatoren', transform: (val, row) => this.getAnzahlAktiveIndikatoren(row) }
      ];

      await this.tableExportService.exportTable({
        data: this.dataSource.data,
        columns: exportColumns,
        filename: 'standorte',
        format: 'xlsx',
        includeHeaders: true
      });

      this.snackBar.open('Standorte erfolgreich als Excel exportiert', 'Schließen', { duration: 3000 });
    } catch (error) {
      console.error('Error exporting standorte:', error);
      this.snackBar.open('Fehler beim Exportieren der Standorte', 'Schließen', { duration: 3000 });
    }
  }

}
